import { Plus, Download, ChevronRight, Lock } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TextField from '@/components/common/TextField'
import RecoveryPhraseInput from '@/components/RecoveryPhraseInput'
import { Info, CheckCircle, XCircle } from 'lucide-react'
import {
  calculatePasswordStrength,
  validatePasswordMatch
} from '@/utils/passwordStrength'
import walletService from '@/services/wallet/wallet.service'
import Snackbar from '@/components/Snackbar'
import Header from '@/components/Layout/Header/Header'

const Welcome = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Detect if wallet exists in localStorage
  const hasSavedWallet = useMemo(() => {
    return (
      localStorage.getItem('multichain_has_wallet') === 'true' ||
      !!localStorage.getItem('wallets_encrypted_data')
    )
  }, [])

  const [activeTab, setActiveTab] = useState<'welcome' | 'import' | 'unlock'>(
    () => {
      return hasSavedWallet ? 'unlock' : 'welcome'
    }
  )

  // Unlock state
  const [password, setPassword] = useState('')
  const [isUnlocking, setIsUnlocking] = useState(false)

  // Import state
  const [importMethod, setImportMethod] = useState<'phrase' | 'key'>('phrase')
  const [isMnemonicValid, setIsMnemonicValid] = useState(false)
  const [importedMnemonic, setImportedMnemonic] = useState('')
  const [importPrivateKey, setImportPrivateKey] = useState('')
  const [importPassword, setImportPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Snackbar state
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  // If already unlocked, redirect to dashboard
  useEffect(() => {
    if (walletService.isWalletUnlocked()) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  const handleCreate = () => {
    navigate('/registration')
  }

  // Calculate password strength
  const passwordStrength = useMemo(
    () => calculatePasswordStrength(importPassword),
    [importPassword]
  )

  // Validate password match
  const passwordMatch = useMemo(
    () => validatePasswordMatch(importPassword, confirmPassword),
    [importPassword, confirmPassword]
  )

  // Check if import form is valid
  const isImportValid =
    (importMethod === 'phrase' ? isMnemonicValid : !!importPrivateKey.trim()) &&
    importPassword.length >= 8 &&
    passwordStrength.score >= 2 &&
    passwordMatch.isValid

  const handleMnemonicValidation = useCallback(
    (isValid: boolean, userMnemonic: string) => {
      setIsMnemonicValid(isValid)
      setImportedMnemonic(userMnemonic)
      console.log('Mnemonic validation:', { isValid, userMnemonic })
    },
    []
  )

  // Maps raw errors to user-friendly i18n keys
  const getFriendlyError = (error: unknown): string => {
    const msg = error instanceof Error ? error.message : String(error)
    if (
      msg.includes('incorrect password') ||
      msg.includes('INVALID_ARGUMENT')
    ) {
      return t('dialogs.wrong_password')
    }
    if (
      msg.includes('invalid mnemonic') ||
      msg.includes('invalid entropy') ||
      msg.includes('Invalid mnemonic')
    ) {
      return t('dialogs.invalid_mnemonic')
    }
    if (msg.includes('invalid private key')) {
      return t('dialogs.invalid_private_key')
    }
    if (
      msg.includes('network') ||
      msg.includes('fetch') ||
      msg.includes('timeout')
    ) {
      return t('dialogs.verify_error')
    }
    return t('dialogs.verify_error')
  }

  const handleImportWallet = async () => {
    if (!isImportValid) return

    try {
      if (importMethod === 'phrase') {
        await walletService.importFromMnemonic(
          importedMnemonic,
          importPassword,
          'Primary Wallet'
        )
      } else {
        const success = await walletService.importFromPrivateKey(
          importPrivateKey.trim(),
          importPassword
        )
        if (!success) {
          throw new Error('invalid private key')
        }
      }
      console.log('Wallet imported and unlocked successfully')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      console.error('Import error:', error)
      setSnackbarState({
        isOpen: true,
        message: getFriendlyError(error),
        type: 'error'
      })
    }
  }

  const handleUnlock = async () => {
    if (!password) return

    try {
      setIsUnlocking(true)
      await walletService.createSession(password)
      console.log('Wallet unlocked successfully')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      console.error('Unlock error:', error)
      setSnackbarState({
        isOpen: true,
        message: getFriendlyError(error),
        type: 'error'
      })
    } finally {
      setIsUnlocking(false)
    }
  }

  const handleBack = () => {
    setActiveTab(hasSavedWallet ? 'unlock' : 'welcome')
  }

  // Get the number of filled bars based on password strength
  const getFilledBars = () => {
    if (!importPassword) return 0
    return passwordStrength.score + 1 // 0-4 becomes 1-5, but we show max 4
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  }

  return (
    <main
      className={`min-h-screen ${activeTab === 'welcome' || 'import' ? 'content-center' : 'content-start'} p-4`}
    >
      <Header
        hideSidebar
        showBackButton={activeTab === 'import'}
        onBack={handleBack}
      />

      <motion.div
        className={`flex flex-col p-4 gap-8 mt-6 relative ${activeTab === 'welcome' ? 'text-center' : ''} min-h-0 lg:max-h-205 lg:max-w-213 place-self-center w-full`}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* UNLOCK VIEW */}
        {activeTab === 'unlock' && (
          <>
            <motion.div
              className="flex flex-col gap-1 items-center"
              variants={itemVariants}
            >
              <img
                src="/assets/omd-wallet.svg"
                alt="OMD Wallet"
                className="w-20 h-20 mb-5"
              />
              <h1 className="text-3xl font-extrabold secondary-gradient-text text-center">
                {t('welcome.unlock_title')}
              </h1>
              <p className="text-lg text-text-main text-center max-w-sm mt-1">
                {t('welcome.unlock_desc')}
              </p>
            </motion.div>

            <motion.div
              className="rounded-2xl flex flex-col gap-6 w-full max-w-sm mx-auto"
              variants={itemVariants}
            >
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-lg text-text-main text-start ml-1">
                  {t('welcome.enter_password')}
                </label>
                <TextField
                  type="password"
                  placeholder={t('welcome.password_input_placeholder')}
                  showPasswordToggle
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && password) {
                      handleUnlock()
                    }
                  }}
                />
              </div>

              <button
                onClick={handleUnlock}
                disabled={isUnlocking || !password}
                className="w-full btn-gradient-fill-secondary rounded-full py-3.5 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
              >
                {isUnlocking ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <div className="flex gap-3 items-center">
                    <Lock className="w-5 h-5" />
                    <p className="font-extrabold text-lg">
                      {t('welcome.unlock_submit')}
                    </p>
                  </div>
                )}
              </button>

              <div className="flex flex-col gap-3 items-center mt-2">
                <button
                  onClick={() => navigate('/reset-wallet')}
                  className="primary-gradient-text hover:underline text-sm font-semibold transition-colors"
                >
                  {t('welcome.forgot_password')}
                </button>
              </div>
            </motion.div>
          </>
        )}

        {/* WELCOME VIEW */}
        {activeTab === 'welcome' && (
          <div className="flex flex-col items-center gap-8">
            <motion.div
              className="flex items-center flex-col gap-1 w-full sm:w-4/5 lg:w-[60%]"
              variants={itemVariants}
            >
              <img
                src="/assets/omd-wallet.svg"
                alt="OMD Wallet"
                className="w-25 h-25 mb-3"
              />
              <h1 className="text-3xl font-extrabold secondary-gradient-text">
                {t('welcome.title')}
              </h1>
              <p className="text-lg text-text-main">{t('welcome.subtitle')}</p>
            </motion.div>

            <motion.button
              className="bg-card rounded-2xl w-full sm:w-4/5 lg:w-[60%] border border-border-base min-h-35 lg:min-h-30 px-4 py-6"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCreate}
            >
              <div className="flex flex-row justify-between items-center">
                <div className="flex gap-4">
                  <motion.div
                    className="bg-card-e2 rounded-full p-3 h-fit"
                    whileHover={{ rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Plus className="w-6 h-6 text-primary" />
                  </motion.div>
                  <div className="flex flex-col gap-1 text-start">
                    <h2 className="font-bold text-text-main text-lg">
                      {t('welcome.create_button')}
                    </h2>
                    <p className="text-text-secondary text-sm">
                      {t('welcome.create_desc')}
                    </p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="text-text-secondary w-5 h-5" />
                </motion.div>
              </div>
            </motion.button>

            <motion.button
              className="bg-card rounded-2xl w-full sm:w-4/5 lg:w-[60%] border border-border-base min-h-35 lg:min-h-30 px-4 py-6"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab('import')}
            >
              <div className="flex flex-row justify-between items-center">
                <div className="flex gap-4">
                  <motion.div
                    className="bg-card-e2 rounded-full p-3 h-fit"
                    whileHover={{ y: -3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Download className="w-6 h-6 text-primary" />
                  </motion.div>
                  <div className="flex flex-col gap-1 text-start">
                    <h2 className="font-bold text-text-main text-lg">
                      {t('welcome.import_button')}
                    </h2>
                    <p className="text-text-secondary text-sm">
                      {t('welcome.import_desc')}
                    </p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="text-text-secondary w-5 h-5" />
                </motion.div>
              </div>
            </motion.button>

            {hasSavedWallet && (
              <motion.button
                onClick={() => setActiveTab('unlock')}
                className="text-primary hover:underline text-sm font-semibold mt-4 transition-colors"
                variants={itemVariants}
              >
                {t('welcome.unlock_submit')} ({t('welcome.unlock_title')})
              </motion.button>
            )}
          </div>
        )}

        {/* IMPORT VIEW */}
        {activeTab === 'import' && (
          <div className="flex flex-col gap-7 w-full mt-12">
            <motion.div
              variants={itemVariants}
              className="flex flex-col gap-1 items-center"
            >
              <img
                src="/assets/omd-wallet.svg"
                alt="OMD Wallet"
                className="w-25 h-25 mb-3"
              />
              <h1 className="text-4xl text-center font-extrabold secondary-gradient-text">
                {t('welcome.import_title')}
              </h1>
            </motion.div>

            {/* BUTTON TABS */}
            <motion.div
              className="rounded-2xl flex flex-col gap-6"
              variants={itemVariants}
            >
              <div className="flex gap-2 bg-card rounded-full p-1 border border-border-base">
                <button
                  type="button"
                  onClick={() => setImportMethod('phrase')}
                  className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
                    importMethod === 'phrase'
                      ? 'btn-gradient-fill-secondary'
                      : 'text-text-secondary'
                  }`}
                >
                  {t('welcome.import_tab_phrase')}
                </button>
                <button
                  type="button"
                  onClick={() => setImportMethod('key')}
                  className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
                    importMethod === 'key'
                      ? 'btn-gradient-fill-secondary'
                      : 'text-text-secondary'
                  }`}
                >
                  {t('welcome.import_tab_key')}
                </button>
              </div>

              {importMethod === 'phrase' ? (
                <RecoveryPhraseInput
                  onValidationChange={handleMnemonicValidation}
                  showValidation={true}
                  title={t('welcome.enter_phrase')}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="font-semibold text-lg text-text-main">
                    {t('welcome.enter_key')}
                  </label>
                  <TextField
                    type="password"
                    showPasswordToggle
                    value={importPrivateKey}
                    onChange={(e) => setImportPrivateKey(e.target.value)}
                    placeholder={'0x...'}
                  />
                </div>
              )}

              {/* Password fields */}
              <div className="flex flex-col gap-2 mt-2">
                <label className="font-semibold text-lg text-text-main">
                  {t('welcome.new_password')}
                </label>
                <TextField
                  type="password"
                  placeholder={t('welcome.password_placeholder')}
                  showPasswordToggle
                  value={importPassword}
                  onChange={(e) => setImportPassword(e.target.value)}
                />
                <label className="font-semibold text-lg text-text-main mt-4">
                  {t('welcome.confirm_password')}
                </label>
                <TextField
                  type="password"
                  placeholder={t('welcome.confirm_placeholder')}
                  showPasswordToggle
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                {/* Password match indicator */}
                {confirmPassword && (
                  <div
                    className={`flex items-center gap-2 mt-2 text-sm ${
                      passwordMatch.isValid ? 'text-success' : 'text-red-500'
                    }`}
                  >
                    {passwordMatch.isValid ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>{t(passwordMatch.message)}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        <span>{t(passwordMatch.message)}</span>
                      </>
                    )}
                  </div>
                )}

                {/* Password strength indicator */}
                <div className="mt-4 flex flex-col bg-card rounded-xl p-5 gap-4">
                  <div className="flex justify-between">
                    <p className="font-medium text-sm text-text-secondary">
                      {t('welcome.password_strength_label')}
                    </p>
                    <p
                      className={`font-bold text-sm ${passwordStrength.color}`}
                    >
                      {t(passwordStrength.label)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className={`w-1/4 h-2 rounded-full transition-all ${
                          index < getFilledBars()
                            ? passwordStrength.score <= 1
                              ? 'bg-red-500'
                              : passwordStrength.score === 2
                                ? 'bg-yellow-500'
                                : 'bg-success'
                            : 'bg-card-e2'
                        }`}
                      />
                    ))}
                  </div>
                  {passwordStrength.suggestions.length > 0 && (
                    <div className="flex gap-2">
                      <Info className="w-4 h-4 text-text-secondary/80 shrink-0 mt-0.5" />
                      <div className="flex flex-col gap-1">
                        {passwordStrength.suggestions.map(
                          (suggestion, index) => (
                            <p
                              key={index}
                              className="text-text-secondary/80 text-xs"
                            >
                              {t(suggestion)}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleImportWallet}
                disabled={!isImportValid}
                className="w-full btn-gradient-fill mb-7 rounded-full py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <p className="font-extrabold text-lg">
                  {t('welcome.import_submit')}
                </p>
              </button>
            </motion.div>
          </div>
        )}
      </motion.div>
      <Snackbar
        message={snackbarState.message}
        isOpen={snackbarState.isOpen}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />
    </main>
  )
}

export default Welcome
